<template>
<list
  :heading="heading"
  :description="description"
  model="grouping"
  :items="groupings"
  :status="status"
  :nextUrl="nextUrl"
  @onFetchMore="updateItems"
  :displayStyle="displayStyle"
>
  <template #filter>
    <p>Total: {{total}} {{plural('grouping', total)}}</p>
    <h2 class="info-section-subheading">Filter Groupings:</h2>
    <button
      class="elr-button elr-button-primary"
      @click="toggleFilters"
    >{{showFilters ? 'Hide' : 'Show'}} Filters</button>
    <div v-show="showFilters">
      <FilterList
        @updated="updateFilters"
        title="Brands"
        field="brandId"
        :items="brands"
        :filters="filters.brandId"
      />
      <FilterList
        @updated="updateFilters"
        title="Origins"
        field="originId"
        :items="origins"
        :filters="filters.originId"
      />
      <FilterList
        @updated="updateFilters"
        title="Generations"
        field="generationId"
        :items="generations"
        :filters="filters.generationId"
      />
      <FilterList
        @updated="updateFilters"
        title="Releases"
        field="releaseId"
        :items="releases"
        :filters="filters.releaseId"
      />
      <FilterList
        @updated="updateFilters"
        title="Manufacturers"
        field="manufacturerId"
        :items="manufacturers"
        :filters="filters.manufacturerId"
      />
      <FilterList
        @updated="updateFilters"
        title="Years"
        field="yearId"
        :items="years"
        :filters="filters.yearId"
      />
      <!-- {{countries}} -->
      <FilterList
        @updated="updateFilters"
        title="Countries"
        field="countryId"
        :items="countries"
        :filters="filters.countryId"
      />
    </div>
    <ViewToggle :displayStyle="displayStyle" @toggled="toggleDisplayStyle" />
    <div class="button-holder sort-list">
      <SortToggle
        field="name"
        :activeField="sort.field"
        :direction="sort.direction"
        buttonLabel="Group Name"
        @sorted="toggleSort"
      />
      <SortToggle
        field="num_ponies"
        :activeField="sort.field"
        :direction="sort.direction"
        buttonLabel="Number Ponies"
        @sorted="toggleSort"
      />
    </div>
  </template>
  <template #list>
    <ul class="item-list">
      <ItemListHeader>
        <span>Name:</span>
        <span>Num Ponies:</span>
        <span>Generation:</span>
        <span>Brand:</span>
        <span>Year:</span>
        <span>Origin:</span>
        <span>Release:</span>
      </ItemListHeader>
      <ListItem
        @removed="removeItem"
        :key="item.id"
        v-for="item in groupings"
        :item="item"
        :user="user"
        model="grouping"
      >
        <span><router-link :to="`/grouping/${item.slug}`">{{item.name}}</router-link></span>
        <span>{{item.num_ponies}} {{plural('Ponies')}}</span>
        <span>{{item.generation.name}}</span>
        <span>{{item.brand.name}}</span>
        <span>{{item.year.year_made}}</span>
        <span>{{item.origin.name}}</span>
        <span>{{item.release.name}}</span>
      </ListItem>
    </ul>
  </template>
  <GroupCard
    :key="item.id"
    v-for="item in groupings"
    :user="user"
    :item="item"
    @removed="removeItem"
  />
</list>
</template>

<script>
import { mapState } from 'vuex';
import { num } from '../../data/config';
import { plural } from '../../utils/helpers';
import List from '../../layouts/List/List';
import ListItem from '../../components/ListItem/ListItem';
import GroupCard from '../../components/GroupCard/GroupCard';
import FilterList from '../../components/FilterList/FilterList';
import ViewToggle from '../../components/ViewToggle/ViewToggle';
import SortToggle from '../../components/SortToggle/SortToggle';
import ItemListHeader from '../../components/ItemListHeader/ItemListHeader';

export default {
  name: 'GroupingList',
  data() {
    return {
      errors: [],
      showFilters: false
    };
  },
  components: {
    GroupCard,
    List,
    FilterList,
    ItemListHeader,
    ListItem,
    SortToggle,
    ViewToggle,
  },
  mounted() {
    if (this.groupings && this.groupings.length < this.total) {
      this.fetchGroupings();
    } else if (!this.total) {
      this.fetchGroupings();
    }
    if (this.brands && this.brands.length < this.brandTotal) {
      this.fetchBrands();
    } else if (!this.brandTotal) {
      this.fetchBrands();
    }
    
    if (this.generations && this.generations.length < this.generationTotal) {
      this.fetchGenerations();
    } else if (!this.generationTotal) {
      this.fetchGenerations();
    }
    
    if (this.releases && this.releases.length < this.releaseTotal) {
      this.fetchReleases();
    } else if (!this.releaseTotal) {
      this.fetchReleases();
    }
    
    if (this.years && this.years.length < this.yearTotal) {
      this.fetchYears();
    } else if (!this.yearTotal) {
      this.fetchYears();
    }
    
    if (this.origins && this.origins.length < this.originTotal) {
      this.fetchOrigins();
    } else if (!this.originTotal) {
      this.fetchOrigins();
    }
    
    if (this.manufacturers && this.manufacturers.length < this.manufacturerTotal) {
      this.fetchManufacturers();
    } else if (!this.manufacturerTotal) {
      this.fetchManufacturers();
    }
    
    if (this.countries && this.countries.length < this.countryTotal) {
      this.fetchCountries();
    } else if (!this.countryTotal) {
      this.fetchCountries();
    }
  },
  computed: {
    ...mapState({
      user: state => state.currentUser.currentUser,
      authenticated: state => state.currentUser.authenticated,
      heading: state => state.grouping.heading,
      description: state => state.grouping.description,
      status: state => state.grouping.status,
      nextUrl: state => state.grouping.nextUrl,
      displayStyle: state => state.grouping.displayStyle,
      sort: state => state.grouping.sort,
      total: state => state.grouping.total,
      brandTotal: state => state.brand.total,
      generationTotal: state => state.generation.total,
      releaseTotal: state => state.release.total,
      yearTotal: state => state.year.total,
      originTotal: state => state.origin.total,
      countryTotal: state => state.country.total,
      manufacturerTotal: state => state.manufacturer.total
    }),
    groupings() {
      return this.$store.getters['grouping/getAll'];
    },
    brands() {
      return this.$store.getters['brand/getAll'];
    },
    generations() {
      return this.$store.getters['generation/getAll'];
    },
    releases() {
      return this.$store.getters['release/getAll'];
    },
    years() {
      return this.$store.getters['year/getAll'];
    },
    origins() {
      return this.$store.getters['origin/getAll'];
    },
    manufacturers() {
      return this.$store.getters['manufacturer/getAll'];
    },
    countries() {
      return this.$store.getters['country/getAll'];
    },
    filters() {
      return this.$store.getters['grouping/getFilters']();
    }
  },
  methods: {
    plural,
    toggleFilters() {
      this.showFilters = (!this.showFilters) ? true : false;
    },
    clearFilters() {
      this.$store.dispatch('pony/CLEAR_FILTERS');
    },
    updateFilters({name, value}) {
      this.$store.dispatch('grouping/UPDATE_FILTERS', {name, value});
    },
    updateItems() {
      this.$store.dispatch('grouping/FETCH_MORE', this.filters);
    },
    removeItem(payload) {
      this.$store.dispatch('grouping/REMOVE', payload)
    },
    url(id) {
      return `/grouping/${id}`
    },
    fetchGroupings() {
      this.$store.dispatch('grouping/FETCH_ALL', {
        num
      });
    },
    fetchBrands() {
      this.$store.dispatch('brand/FETCH_ALL', {
        num: -1
      });
    },
    fetchGenerations() {
      this.$store.dispatch('generation/FETCH_ALL', {
        num: -1
      });
    },
    fetchReleases() {
      this.$store.dispatch('release/FETCH_ALL', {
        num: -1
      });
    },
    fetchYears() {
      this.$store.dispatch('year/FETCH_ALL', {
        num: -1
      });
    },
    fetchOrigins() {
      this.$store.dispatch('origin/FETCH_ALL', {
        num: -1
      });
    },
    fetchManufacturers() {
      this.$store.dispatch('manufacturer/FETCH_ALL', {
        num: -1
      });
    },
    fetchCountries() {
      this.$store.dispatch('country/FETCH_ALL', {
        num: -1
      });
    },
    toggleDisplayStyle({ display }) {
      this.$store.dispatch('grouping/CHANGE_DISPLAY_STYLE', { display })
    },
    toggleSort({ direction, field }) {
      this.$store.dispatch('grouping/SORT', { direction, field });
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../assets/sass/_config.scss';

.item-list {
  margin: 0;
  padding: 0;
}

.button-holder {
  text-align: left;
  padding-top: 0;
  
  .sort-button {
    margin-right: 10px;
    width: auto;
  }
}

</style>
